// client/src/components/hooks/useAuth.js
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { getLogger } from '@/utils/logger';
import { AuthStateContext, fallbackAuthState } from '@/components/Auth/AuthStateContext';

const log = getLogger('useAuth');
const CLAIM_NAMESPACE = 'https://jv.landivo.com';

const decodeJwtPayload = (accessToken) => {
  try {
    const tokenParts = accessToken.split('.');
    if (tokenParts.length !== 3) {
      return null;
    }
    const base64Url = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64Url.padEnd(Math.ceil(base64Url.length / 4) * 4, '=');
    return JSON.parse(atob(padded));
  } catch (error) {
    log.error(
      `[useAuth:decodeJwtPayload] > [Request]: parseToken > [Response]: error=${error?.message || "unknown"}`
    );
    return null;
  }
};

// Roles and permissions both arrive via Keycloak protocol mappers
const resolveRolesFromPayload = (payload) => {
  if (!payload) {
    return { roles: [], source: 'none' };
  }

  if (Array.isArray(payload[`${CLAIM_NAMESPACE}/roles`])) {
    return { roles: payload[`${CLAIM_NAMESPACE}/roles`], source: `${CLAIM_NAMESPACE}/roles` };
  }

  if (Array.isArray(payload.roles)) {
    return { roles: payload.roles, source: 'payload.roles' };
  }

  if (Array.isArray(payload[CLAIM_NAMESPACE]?.roles)) {
    return { roles: payload[CLAIM_NAMESPACE].roles, source: `${CLAIM_NAMESPACE}.roles` };
  }

  return { roles: [], source: 'none' };
};

const resolvePermissionsFromPayload = (payload) => {
  if (!payload) {
    return { permissions: [], source: 'none' };
  }

  if (Array.isArray(payload.permissions)) {
    return { permissions: payload.permissions, source: 'payload.permissions' };
  }

  if (Array.isArray(payload[`${CLAIM_NAMESPACE}/permissions`])) {
    return {
      permissions: payload[`${CLAIM_NAMESPACE}/permissions`],
      source: `${CLAIM_NAMESPACE}/permissions`,
    };
  }

  if (Array.isArray(payload[CLAIM_NAMESPACE]?.permissions)) {
    return {
      permissions: payload[CLAIM_NAMESPACE].permissions,
      source: `${CLAIM_NAMESPACE}.permissions`,
    };
  }

  return { permissions: [], source: 'none' };
};


export function useAuthLegacy() {
  const {
    isAuthenticated,
    isLoading,
    user,
    signinRedirect,
    signoutRedirect,
    signinSilent,
  } = useOidcAuth();

  const accessTokenPayload = useMemo(
    () => (user?.access_token ? decodeJwtPayload(user.access_token) : null),
    [user?.access_token]
  );

  const roleResolution = useMemo(() => resolveRolesFromPayload(accessTokenPayload), [accessTokenPayload]);
  const permissionResolution = useMemo(
    () => resolvePermissionsFromPayload(accessTokenPayload),
    [accessTokenPayload]
  );
  const userRoles = roleResolution.roles;
  const userPermissions = permissionResolution.permissions;

  // Calculate admin/agent status from roles
  const { isAdmin, isAgent } = useMemo(() => {
    return {
      isAdmin: userRoles.includes('Admin'),
      isAgent: userRoles.includes('Agent')
    };
  }, [userRoles]);

  // Log role/permission extraction from the identity token
  useEffect(() => {
    if (!user) {
      if (!isLoading) {
        log.info("[useAuth:extractRoles] > [Request]: readRoles > [Response]: none");
      }
      return;
    }

    log.info(
      `[useAuth:extractRoles] > [Request]: readIdentity > [Response]: identityLoaded > [Comment]: sub=${user.profile?.sub || "unknown"}`
    );
    log.info(
      roleResolution.source !== 'none'
        ? `[useAuth:extractRoles] > [Request]: readRoles > [Response]: found > [Comment]: source=${roleResolution.source} roles=${JSON.stringify(roleResolution.roles)}`
        : "[useAuth:extractRoles] > [Request]: readRoles > [Response]: none"
    );
    log.info(
      permissionResolution.source !== 'none'
        ? `[useAuth:extractRoles] > [Request]: readPermissions > [Response]: found > [Comment]: source=${permissionResolution.source} permissions=${JSON.stringify(permissionResolution.permissions)}`
        : "[useAuth:extractRoles] > [Request]: readPermissions > [Response]: none"
    );
  }, [user, isLoading, roleResolution, permissionResolution]);

  // Function to get auth token for API calls, translating the Auth0-flavored
  // option shape ({cacheMode, ignoreCache, forceFresh}) that the rest of the
  // auth lifecycle layer (AuthStateProvider/TokenValidationService) already
  // calls this with into oidc-client-ts operations.
  const getTokenOrThrow = useCallback(async (options = undefined) => {
    if (!isAuthenticated) {
      throw new Error('Account is not authenticated');
    }

    if (options?.cacheMode === 'cache-only') {
      // Cache-only read: return whatever token we currently have without
      // triggering a network refresh; the caller checks staleness itself.
      return user?.access_token || null;
    }

    const forceRefresh =
      options?.cacheMode === 'off' || options?.ignoreCache === true || options?.forceFresh === true;

    if (forceRefresh) {
      try {
        const refreshedUser = await signinSilent();
        return refreshedUser?.access_token || null;
      } catch (error) {
        // Normalize to the standard OAuth2 error codes TokenValidationService
        // already knows how to interpret - Keycloak returns the same ones Auth0 did.
        if (!error.error && /login required|no matching state/i.test(error?.message || '')) {
          error.error = 'login_required';
        }
        throw error;
      }
    }

    if (!user?.access_token) {
      throw new Error('Missing Refresh Token');
    }

    return user.access_token;
  }, [isAuthenticated, user, signinSilent]);

  // Function to get auth token for API calls
  const getToken = useCallback(async (options = undefined) => {
    if (!isAuthenticated) return null;

    try {
      return await getTokenOrThrow(options);
    } catch (error) {
      log.error(
        `[useAuth:getToken] > [Request]: acquireToken > [Response]: error=${error?.message || "unknown"}`
      );
      return null;
    }
  }, [isAuthenticated, getTokenOrThrow]);

  const loginWithRedirect = useCallback((options = {}) => {
    // login_hint is a standard OIDC param Keycloak honors (pre-fills the
    // username field). screen_hint (Auth0's "jump straight to sign-up tab")
    // has no Keycloak equivalent via the standard authorization endpoint -
    // Keycloak's login page shows a "Register" link instead, so it's dropped
    // rather than silently ignored deep inside oidc-client-ts.
    const extraQueryParams = {};
    if (options?.authorizationParams?.login_hint) {
      extraQueryParams.login_hint = options.authorizationParams.login_hint;
    }

    return signinRedirect({
      state: { returnTo: options?.appState?.returnTo },
      ...(Object.keys(extraQueryParams).length ? { extraQueryParams } : {}),
    });
  }, [signinRedirect]);

  const logout = useCallback((options = {}) => {
    const returnTo = options?.logoutParams?.returnTo || window.location.origin;
    return signoutRedirect({ post_logout_redirect_uri: returnTo });
  }, [signoutRedirect]);

  return useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      identityUser: user?.profile ? { ...user.profile, needsProfileCompletion: false } : null,
      userRoles,
      userPermissions,
      isAdmin,
      isAgent,
      needsProfileCompletion: false,
      loginWithRedirect,
      logout,
      getToken,
      getTokenOrThrow,
    }),
    [
      isAuthenticated,
      isLoading,
      user,
      userRoles,
      userPermissions,
      isAdmin,
      isAgent,
      loginWithRedirect,
      logout,
      getToken,
      getTokenOrThrow,
    ]
  );
}

export function useAuth() {
  const authState = useContext(AuthStateContext);
  if (authState === fallbackAuthState) {
    log.error(
      '[useAuth] > [Response]: AuthStateProvider missing; using fallback auth state'
    );
  }
  return authState;
}
